import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Always rotates automatically (no pause on hover/touch).
 *
 * @extends {Component}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /** @type {number|undefined} */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  next() {
    this.current += 1;
    this.#restart();
  }

  previous() {
    this.current -= 1;
    this.#restart();
  }

  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;
    clearInterval(this.#interval);

    this.#interval = setInterval(() => {
      if (document.hidden) return;

      this.current += 1;
    }, interval);
  }

  #restart() {
    if (this.#interval !== undefined) this.play();
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  resume() {
    if (!this.autoplay) return;

    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
